import { Maybe } from "true-myth";
import { Blockchain, blockchainsConfig, ChainEnvironment, ChainId } from "./blockchains.config";
import { Strategy } from "./contracts";
import { bigDecimal, BigDecimal } from "./utils/bigDecimal";
import { readChainEnvironment, readEnvVar, UnsupportedChainEnvError } from "./utils/env";

const strategyDisplayNames: Record<Strategy, string> = {
    [Strategy.Alpha1TheCPPIooorV1]: "Alpha #1 - The CPPIooor V1 (Avalanche Mainnet)",
    [Strategy.Alpha1TheCPPIooorV2]: "Alpha #1 - The CPPIooor V2 (Avalanche Mainnet)",
    [Strategy.Alpha2SmartFarmooor]: "Alpha #2 - Smart Farmooor (Avalanche Mainnet)",
};

export enum EnvVar {
    ChainEnvironment = "CHAIN_ENV",
    StrategyAddress = "ADDRESS",
    DepositTokenAddress = "DEPOSIT_TOKEN_ADDRESS",
    MigratorAddress = "MIGRATOR_ADDRESS",
    MinDepositAmount = "MIN_DEPOSIT_AMOUNT",
    MaxDepositAmount = "MAX_DEPOSIT_AMOUNT",
}

interface ChainConfig {
    blockchain: Blockchain;
    environment: ChainEnvironment;
    id: ChainId;
    explorerUrl?: string;
}

export interface StrategyProperties {
    strategy: Strategy;
    name: string;
    chain: ChainConfig;
    address: string;
    depositTokenAddress: string | null;
    migratorAddress: string | null;
    minDepositAmount: BigDecimal | null;
    maxDepositAmount: BigDecimal | null;
}

export type StrategiesConfig = Record<Strategy, StrategyProperties>;

const strategyBlockchainMapping: Record<Strategy, Blockchain> = {
    [Strategy.Alpha1TheCPPIooorV1]: Blockchain.Avalanche,
    [Strategy.Alpha1TheCPPIooorV2]: Blockchain.Avalanche,
    [Strategy.Alpha2SmartFarmooor]: Blockchain.Avalanche,
};

const readChainConfig = (strategy: Strategy): ChainConfig => {
    const blockchain = strategyBlockchainMapping[strategy];
    const environment = readChainEnvironment(strategy);
    const { id, explorerUrl } = blockchainsConfig[blockchain][environment] ?? {};
    if (id === undefined) throw UnsupportedChainEnvError(strategy, blockchain, environment);

    return {
        environment,
        id,
        blockchain,
        explorerUrl,
    };
};

const readStrategyProperties = (strategy: Strategy): StrategyProperties => ({
    strategy,
    name: strategyDisplayNames[strategy],
    address: readEnvVar(strategy, EnvVar.StrategyAddress),
    chain: readChainConfig(strategy),
    depositTokenAddress: readEnvVar(strategy, EnvVar.DepositTokenAddress, false),
    migratorAddress: readEnvVar(strategy, EnvVar.MigratorAddress, false),
    minDepositAmount: Maybe.of(readEnvVar(strategy, EnvVar.MinDepositAmount, false)).mapOr(null, bigDecimal),
    maxDepositAmount: Maybe.of(readEnvVar(strategy, EnvVar.MaxDepositAmount, false)).mapOr(null, bigDecimal),
});

export const strategiesConfig: StrategiesConfig = {
    [Strategy.Alpha1TheCPPIooorV1]: readStrategyProperties(Strategy.Alpha1TheCPPIooorV1),
    [Strategy.Alpha1TheCPPIooorV2]: readStrategyProperties(Strategy.Alpha1TheCPPIooorV2),
    [Strategy.Alpha2SmartFarmooor]: readStrategyProperties(Strategy.Alpha2SmartFarmooor),
};
